// Session 53 · Onboarding Resume Banner.
//
// Bright top-of-page banner that appears for any signed-in user with an
// unpublished draft venue. Nudges them back to /owner/onboard at the exact
// step they left, so a half-set-up venue never gets forgotten.
//
// Behavior:
//   * Polls /api/owner/onboarding/state ONCE per session (cached to reduce
//     traffic; refreshed on route change).
//   * Hides itself on the /owner/onboard route (would be redundant).
//   * Includes a dismiss (×) that suppresses the banner for the current
//     browser tab only — refresh brings it back so hosts aren't stuck.
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const STEP_NAMES = ["", "Business", "Address", "Workspaces", "Booking", "Payouts", "Review"];

export default function OnboardingResumeBanner() {
  const { user } = useAuth();
  const location = useLocation();
  const [state, setState] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user) { setState(null); return; }
    // Skip the check on the wizard itself + login/oauth pages
    if (location.pathname.startsWith("/owner/onboard")
        || location.pathname.startsWith("/login")
        || location.pathname.startsWith("/signup")) return;
    let cancelled = false;
    api.get("/owner/onboarding/state")
      .then(r => { if (!cancelled) setState(r.data); })
      .catch(() => { if (!cancelled) setState(null); });
    return () => { cancelled = true; };
  }, [user, location.pathname]);

  // Hide when no draft OR draft already published
  if (!user || !state?.venue || state.venue.is_draft !== true || dismissed) return null;

  const currentStep = Math.min(Math.max(state.current_step || 1, 1), 6);
  const stepName = STEP_NAMES[currentStep] || "Setup";
  const venueName = state.venue.name || "your venue";
  const percent = Math.round((currentStep / 6) * 100);

  return (
    <div data-testid="onboarding-resume-banner"
        style={{
          background: "linear-gradient(90deg, #B8913A 0%, #D4A84B 100%)",
          color: "#fff",
          padding: "10px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 20,
          flexWrap: "wrap",
          position: "sticky",
          top: 0,
          zIndex: 40,
          boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
        }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <span style={{ fontSize: 20 }} aria-hidden="true">🏗️</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.01em" }}
                data-testid="onboarding-resume-title">
            Continue setup — Step {currentStep} of 6: {stepName}
          </div>
          <div style={{ fontSize: 11, opacity: 0.9, marginTop: 2 }}>
            {venueName} · {percent}% complete
          </div>
        </div>
        {/* Compact progress bar */}
        <div style={{
          width: 120, height: 6, borderRadius: 999,
          background: "rgba(255,255,255,0.25)", overflow: "hidden",
        }} aria-hidden="true">
          <div style={{
            width: `${percent}%`, height: "100%", background: "#fff",
            transition: "width 0.3s ease",
          }}/>
        </div>
      </div>
      <Link to="/owner/onboard" data-testid="onboarding-resume-cta"
        style={{
          background: "#0f1729", color: "#fff",
          padding: "8px 18px", borderRadius: 999,
          fontSize: 12, fontWeight: 700, letterSpacing: "0.06em",
          textTransform: "uppercase", textDecoration: "none",
          border: "1px solid rgba(255,255,255,0.35)",
        }}>
        Resume →
      </Link>
      <button type="button" onClick={() => setDismissed(true)}
        data-testid="onboarding-resume-dismiss"
        aria-label="Dismiss for this session"
        style={{
          background: "none", border: "none", color: "rgba(255,255,255,0.85)",
          fontSize: 20, lineHeight: 1, cursor: "pointer", padding: "0 4px",
        }}>×</button>
    </div>
  );
}
